// Electorate report body — A4 (794 x 1123 px @96dpi), id `report-content-{area_id}`.
// Real data where Phase 0 marked AVAILABLE; #8 overlap is focal-driven and live;
// attack frames is a slim labelled "coming soon" strip (not yet modelled).
// Visual language follows the Constituency Profiles reference (teal accents, small-caps
// eyebrows, accent-strip cards, value + delta-bar, muted national comparisons).
//
// Data path reproduces the main app's numbers exactly: loadGroupData pins one wave via
// filters:{wave} + turnout_1_n, the same per-wave fetch the main app makes. Every wave is
// fetched and merged newest-wins-where-non-null, so a battery that only ran in one wave
// still shows. No normalisation (values are shares).
//
// Value scales (confirmed against the endpoint):
//   *_pred_n, past_vote_*, *_issue_n, segment fields  -> already 0-100 (%)
//   *_consider                                         -> stored x100 of a 0-10 scale (/10 => %)
// Softness = consideration(%) - projected vote(%) = (consider/10) - pred_n.
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Stack, Paper, CircularProgress } from "@mui/material";
import { PARTIES } from "./constants/partyConfig";
import { ISSUE_INFO } from "./constants/issueConfig";
import { SEGMENTS } from "./constants/segmentConfig";
import { loadGroupData, loadRows } from "./utils/reportData";
import { WAVES } from "./constants/waves";
import SVGChoropleth from "./SVGChoropleth";

/* ── design tokens ─────────────────────────────────────────────── */
const TEAL = "#0abfbc";
const INK = "#0f172a";
const INK2 = "#64748b";
const INK3 = "#94a3b8";
const BORDER = "#e2e8f0";
const SURF = "#f8fafc";
const POS = "#15803d";
const NEG = "#b91c1c";

/* ── themed group accents (muted/professional; applied to the card accent strip + eyebrow
   ONLY — never the party bars, choropleth map fills, or POS/NEG arrows). One family shared
   across both pages: MAPS/Messages = slate, Vote/Coalition = teal, Issues = ochre,
   Overlap/Opposition = plum. ─────────────────────────────────────── */
const ACCENT_MAPS = "#4A6785";    // slate blue-grey  — page-1 maps, page-2 messages
const ACCENT_VOTE = "#2A9D8F";    // muted teal       — page-1 vote row, page-2 coalition
const ACCENT_ISSUES = "#C77D3A";  // muted amber/ochre — page-1 issues + trust
const ACCENT_OVERLAP = "#6D5B97"; // deep plum/violet  — page-1 overlap band, page-2 opposition

/* ── assets (public/static/images) ─────────────────────────────── */
const IMG = `${process.env.PUBLIC_URL || ""}/static/images`;
const STACK_LOGO = `${IMG}/stack_logo.png`;
// Maps (#3/#4), header winner, and legend use the fixed five — unchanged.
const RP = ["lab", "nat", "green", "nzf", "act"].map((k) => ({ key: k, ...PARTIES[k] }));
// #5 (projected VI) & #6 (softness): ALL SIX parties, ranked by projected VI descending, top-N
// shown flat. TPM competes on equal footing — no special-case; it appears iff its VI ranks it in.
const BAR_PARTIES = ["lab", "nat", "green", "nzf", "act", "tpm", "top"].map((k) => ({ key: k, ...PARTIES[k] }));
const BAR_N = 5; // flat cap on rows shown in #5/#6 (six parties compete for these slots)
const PRED_KEYS = BAR_PARTIES.map((p) => p.field);          // superset of RP preds (incl tpm_pred_n)
const PV_KEYS = RP.map((p) => p.pastVoteField); // 5 shown parties (used for SA2 winner argmax)
// Denominator for the 2023 baseline correction: ALL parties (incl TPM/Other), NOT just the
// five shown. past_vote_* is a share over the all-respondents base (7 parties + non-voter,
// sums to 100); dividing by the party total = (1 - non-voter share) re-bases to voters only,
// matching official party-vote share. Using only the 5 shown parties would overstate.
const PV_DENOM_KEYS = ["lab", "nat", "green", "nzf", "act", "tpm", "oth"].map((k) => PARTIES[k].pastVoteField);
const CONSIDER_KEYS = BAR_PARTIES.map((p) => `${p.key}_consider`); // incl tpm_consider
const ISSUE_KEYS = Object.keys(ISSUE_INFO);
const SEG_KEYS = Object.values(SEGMENTS).map((s) => s.field);
/* ── PAGE 2 field config — all fields are ALREADY 0–100 percentages; consume AS-IS
   (no ×10 / ×100 / normalisation). Keys are added to the existing loadGroupData request
   (Stage 1 confirmed the aggregate surfaces them) — no new fetch. ─────────────────── */
// Coalition policies: net support = {stem}_support_vi_n − {stem}_oppose_vi_n (pp, can be −ve).
// Labels match the survey support_* battery (QIDs 343-350). Only these 6 have _support_vi_n
// fields in the aggregate; the survey's other two support items (doctor_visits QID 346, capital
// gains QID 349) aren't surfaced in the data, so they're not shown.
const COALITION_POLICIES = [
  { label: "NZ-India FTA", stem: "india_agreement" },        // NZ-India free trade agreement (QID 348)
  { label: "RMA overhaul", stem: "overhaul_rma" },           // overhaul the Resource Management Act (QID 350)
  { label: "Gang patch ban", stem: "gang_patches" },         // ban gang patches in public (QID 347)
  { label: "Phone ban in schools", stem: "banning_phones" }, // ban mobile phones in schools (QID 343)
  { label: "KiwiSaver 6%", stem: "kiwisaver" },              // KiwiSaver contributions to 6% by 2032 (QID 345)
  { label: "1hr core subjects", stem: "primary_schools" },   // 1hr/day reading, writing & maths (QID 344)
];
// Opposition policies: single {field} concern share, shown AS-IS (0–100). Labels match the
// policy_concern options (QID 399: which Labour/Green/TPM-backed policies concern you). Two cards.
const OPP_POLICIES_LEFT = [
  { label: "Abolish prisons", field: "abolish_prisons_n" },
  { label: "Replace prisons", field: "replace_prisons_n" },
  { label: "Increase income tax", field: "income_tax_n" },
  { label: "Inheritance tax", field: "inheritance_tax_n" },
  { label: "Demilitarize police", field: "demil_police_n" },
  { label: "Wealth tax", field: "wealth_tax_n" },
];
const OPP_POLICIES_RIGHT = [
  { label: "Free public transport", field: "fare_free_public_transport_n" },
  { label: "28% cap gains tax", field: "cgt_properties_n" },
  { label: "Climate taxes", field: "climate_taxes_n" },
  { label: "UBI", field: "ubi_n" },
  { label: "Company tax", field: "company_tax_n" },
  { label: "Land tax", field: "land_tax_undeveloped_n" },
];
// Message testing: {family}_msg1..6_won_n (0–100 "won" share, INDEPENDENT: do not assume Σ100).
// Texts match the survey MAXDIFF batteries verbatim: coalitionChaos = QID 368, labAttack = QID 370,
// coalBelieve (coalition performance) = QID 371.
const MESSAGE_FAMILIES = [
  {
    title: "Coalition of Chaos", family: "coalitionChaos",
    texts: {
      1: "What Hipkins rules out today, he'll sign up to tomorrow",
      2: "A fragile coalition - with unreliable partners - could collapse when New Zealand needs stability",
      3: "You vote for Hipkins, you get Swarbrick and Waititi",
      4: "Labour, Greens, Te Pāti Māori - a government that would spend more time fighting itself than fighting for you",
      5: "You won't find out what you voted for until they've finished haggling behind closed doors",
      6: "Labour would be dragged to the left and implement a universal basic income, a wealth tax, and abolish prisons",
    },
  },
  {
    title: "Labour Attack", family: "labAttack",
    texts: {
      1: "Labour promised 100,000 homes. They delivered 1,500. Why would next time be any different?",
      2: "A return to Labour risks another education crisis, with too many kids missing school and falling behind - just like last time",
      3: "Labour let gangs and violent crime get out of control - they can't be trusted on law and order",
      4: "Under Labour, Kiwis waited longer for cancer scans and treatment. They can't afford to wait again",
      5: "The coalition is building roads, schools, and hospitals - Labour would put those projects on hold",
      6: "Labour would bring back the bloated bureaucracy and wasteful spending the coalition has cut",
    },
  },
  {
    title: "Coalition performance", family: "coalBelieve",
    texts: {
      1: "The coalition has fixed the basics and is now building for the future",
      2: "The coalition has cracked down on gangs and made New Zealand safer",
      3: "The economy has turned the corner because the government reined in wasteful spending and kept inflation down",
      4: "By cutting red tape and reforming planning laws, the coalition is unlocking more homes and infrastructure",
      5: "The coalition is fixing the basics in education - banning phones, mandating reading, writing and maths, and improving literacy results",
      6: "The coalition is growing the economy by backing exporters, investment, and productive businesses",
    },
  },
];
// ── NEW final-slot data (all wave 3, electorate + SA2, 0–100 already; consume AS-IS) ──────
// Overlap: % rating the focal coalition party AND an opposition party ≥6 — one % per focal
// party (nat/nzf/act), available electorate AND per-SA2.
const OVERLAP_KEYS = ["nat_overlap_n", "nzf_overlap_n", "act_overlap_n"];
// Budget '26 — 7 item net approval = vi_{stem}_good_n − vi_{stem}_bad_n (more-likely − less-likely
// to vote for a party that supported the measure), mirroring the coalition net so the box matches
// the neighbouring policy card. Labels match the actual Budget 2026 survey measures (QIDs 259-265).
const BUDGET_ITEMS = [
  { label: "In-work tax credit", stem: "work_tax" },      // +$50/wk In-Work Tax Credit (QID 264)
  { label: "Pay equity changes", stem: "pay_eq" },        // keep pay-equity scheme changes (QID 265)
  { label: "Scrap fees-free", stem: "scrap_fees" },       // scrap fees-free tertiary study (QID 262)
  { label: "Public service cuts", stem: "reduce_public" },// cut ~8,700 public-service jobs (QID 260)
  { label: "Social housing rent", stem: "social_rent" },  // social rent 25% → 30% of income (QID 261)
  { label: "Health funding", stem: "health_serv" },       // $5.5b frontline health funding (QID 259)
  { label: "Return to surplus", stem: "surplus" },        // books to surplus by 2028/29 (QID 263)
];
const BUDGET_KEYS = BUDGET_ITEMS.flatMap((p) => [`vi_${p.stem}_good_n`, `vi_${p.stem}_bad_n`]);
// Approval doughnuts (top of page 2): government approval + overall budget approval, each
// approve / disapprove / don't-know. gov_* is in both waves (→ wave 3 latest); vi_budget_* wave 3.
const GOV_KEYS = ["gov_good_n", "gov_bad_n", "gov_dn_n"];
const BUDGET_OVERALL_KEYS = ["vi_budget_good_n", "vi_budget_bad_n", "vi_budget_dk_n"];
// Trust by issue: coalition / opposition / neither. Keyed to the page-1 issue field so the trust
// box aligns to the top-5 issues table. Only these 8 issues have trust data.
const ISSUE_TO_TRUST = {
  inflation_issue_n: "inflation", health_issue_n: "healthcare", econ_issue_n: "econ",
  crime_issue_n: "crime", housing_issue_n: "housing", poverty_issue_n: "poverty",
  unemployment_issue_n: "unemployment", climate_issue_n: "climate",
};
const TRUST_KEYS = Object.values(ISSUE_TO_TRUST).flatMap((s) => [`vi_${s}_coalition_n`, `vi_${s}_opposition_n`, `vi_${s}_none_n`]);
// Trust segment colours — muted, distinct, not exact party colours.
const TRUST_COAL = "#3b6ea5", TRUST_OPP = "#b5514f", TRUST_NONE = "#c9d1dc";

// All page-2 keys, appended to the existing loadGroupData request.
const PAGE2_COAL_KEYS = COALITION_POLICIES.flatMap((p) => [`${p.stem}_support_vi_n`, `${p.stem}_oppose_vi_n`]);
const PAGE2_OPP_KEYS = [...OPP_POLICIES_LEFT, ...OPP_POLICIES_RIGHT].map((p) => p.field);
const PAGE2_MSG_KEYS = MESSAGE_FAMILIES.flatMap((f) => Array.from({ length: 6 }, (_, i) => `${f.family}_msg${i + 1}_won_n`));
const PAGE2_KEYS = [...PAGE2_COAL_KEYS, ...PAGE2_OPP_KEYS, ...PAGE2_MSG_KEYS, ...OVERLAP_KEYS, ...BUDGET_KEYS, ...TRUST_KEYS, ...GOV_KEYS, ...BUDGET_OVERALL_KEYS];
// Batteries asked ONLY in wave 2 (policy support / opposition / messages). The wave-3 aggregate
// returns 0 — not null — for these un-asked columns, so they must NOT be overlaid from wave 3
// during the per-field merge or they'd clobber the real wave-2 values with zeros.

// Number, or null when there is genuinely no value.
//
// The null check is not redundant: Number(null) === 0 and Number("") === 0, both
// finite, so the obvious one-liner turns "this battery was not asked in this wave"
// into a real zero. That is what made the cross-wave merge blank out every field
// that should have fallen back to an earlier wave — a later wave's null became 0,
// and 0 is not nullish, so ?? never reached the older value.
const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
function mix(hex, t) {
  const c = String(hex).replace("#", "");
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  const k = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(255 + (r - 255) * k)},${Math.round(255 + (g - 255) * k)},${Math.round(255 + (b - 255) * k)})`;
}
// Linear interpolation between two hex colours (t: 0→a, 1→b). Used for the higher-contrast
// overlap-concentration ramp (light teal → dark teal).
function mix2(aHex, bHex, t) {
  const p = (h) => { const c = String(h).replace("#", ""); return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)]; };
  const [ar, ag, ab] = p(aHex), [br, bg, bb] = p(bHex);
  const k = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(ar + (br - ar) * k)},${Math.round(ag + (bg - ag) * k)},${Math.round(ab + (bb - ab) * k)})`;
}
const OVERLAP_LO = "#e6f4f3", OVERLAP_HI = "#0a6b69"; // overlap map ramp endpoints (light → dark teal)
// Merge SA2 rows from two waves by area_id, per-field most-recent (wave-3) non-null wins — so a
// field present only in one wave still shows, and refreshed fields take the newer value.
// Merge per-SA2 rows across waves, oldest first — a later non-null value wins,
// so each field shows its most recent real figure and falls back as far as needed.
function mergeSa2(...waveRows) {
  const byId = new Map();
  waveRows.forEach((rows) => {
    (rows || []).forEach((r) => {
      const id = String(r.area_id);
      const merged = byId.has(id) ? { ...byId.get(id) } : { ...r };
      Object.keys(r).forEach((k) => { if (r[k] != null) merged[k] = r[k]; });
      byId.set(id, merged);
    });
  });
  return [...byId.values()];
}
// Top-SA2 table (page 1, lower-right) — shared column spec so header sits over columns.
const SA2_COLS = { rank: 16, overlap: 34, y2023: 40, vi: 38 };
// DISPLAY-ONLY: is this SA2 an offshore island? The `island` flag may arrive as a JSON
// boolean, 1, or "true"/"TRUE"/"1" depending on how st_write/GDAL encoded the logical.
// Missing/0/false => mainland (kept). Used to trim the REPORT maps' extent only — never
// the data layer (island voters stay in every numeric total via the loadGroupData aggregate).
const isIslandFeature = (f) => {
  const v = f?.properties?.island;
  return v === true || v === 1 || (typeof v === "string" && ["true", "1"].includes(v.trim().toLowerCase()));
};

/* ── presentational primitives ─────────────────────────────────── */
const Eyebrow = ({ color = TEAL, children, sx }) => (
  <Typography sx={{ fontSize: 7.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color, lineHeight: 1.1, ...sx }}>
    {children}
  </Typography>
);
const AccentCard = ({ accent = TEAL, eyebrow, children, side = false, sx }) => (
  <Box
    sx={{
      position: "relative", bgcolor: "#fff", border: `1px solid ${BORDER}`, borderRadius: "10px",
      ...(side ? { borderLeft: `3px solid ${accent}` } : { borderTop: `3px solid ${accent}` }),
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: "8px 11px", boxSizing: "border-box", ...sx,
    }}
  >
    {eyebrow && <Eyebrow color={accent} sx={{ mb: "5px" }}>{eyebrow}</Eyebrow>}
    {children}
  </Box>
);
const ResultPill = ({ eyebrow, color, name, detail }) => (
  <Box sx={{ display: "flex", flexDirection: "column", border: `1px solid ${BORDER}`, borderRadius: "8px", px: 1, py: 0.5, bgcolor: SURF, minWidth: 112 }}>
    <Eyebrow color={INK3} sx={{ fontSize: 6.5 }}>{eyebrow}</Eyebrow>
    <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: "1px" }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 10, fontWeight: 800, color: INK, whiteSpace: "nowrap" }}>{name}</Typography>
      <Typography sx={{ fontSize: 8.5, color: INK2, whiteSpace: "nowrap" }}>{detail}</Typography>
    </Stack>
  </Box>
);
const Delta = ({ v }) => {
  if (v == null) return <Box sx={{ width: 66 }} />;
  const pos = v >= 0;
  const w = Math.min(50, Math.abs(v) * 3.2);
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: 66, justifyContent: "flex-end" }}>
      <Box sx={{ position: "relative", width: 30, height: 6, bgcolor: "#eef2f7", borderRadius: 3 }}>
        <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: INK3 }} />
        <Box sx={{ position: "absolute", top: 0, height: "100%", borderRadius: 3, width: `${w}%`, bgcolor: pos ? POS : NEG, ...(pos ? { left: "50%" } : { right: "50%" }) }} />
      </Box>
      <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: pos ? POS : NEG, width: 32, textAlign: "right" }}>{pos ? "+" : ""}{v.toFixed(0)}%</Typography>
    </Stack>
  );
};

// Shared report banner — rendered IDENTICALLY at the top of page 1 and page 2 (single source
// of truth so the two pages can't drift). Logo · electorate name · focal-party chip · VI/2023
// pills. Purely presentational; all figures come in as props.
const ReportBanner = ({ areaName, focalParty, proj, w23, margin2023 }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box>
        {/* <img src={STACK_LOGO} alt="Stack Data Strategy" crossOrigin="anonymous" style={{ height: 26, objectFit: "contain", display: "block" }} /> */}
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.05, mt: "6px" }}>{areaName}</Typography>
        <Box sx={{ mt: "3px", width: 120, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${TEAL}, #facc15)` }} />
      </Box>
      <Stack alignItems="flex-end" spacing={0.75}>
        {/* <Typography sx={{ fontSize: 9, color: INK2 }}>Prepared by Stack Data Strategy</Typography> */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: "flex", alignItems: "center", px: 1.25, py: 0.75, borderRadius: "8px", bgcolor: mix(focalParty.color, 0.14), border: `1px solid ${focalParty.color}` }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: focalParty.color, mr: 0.6 }} />
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: focalParty.color, whiteSpace: "nowrap" }}>{focalParty.name}</Typography>
          </Box>
          {proj && <ResultPill eyebrow="VI Projection" color={proj.color} name={proj.name} detail={`${proj.v.toFixed(0)}%`} />}
          {w23 && <ResultPill eyebrow="2023 Result" color={w23.color} name={w23.name} detail={`${w23.v.toFixed(0)}% · maj ${margin2023 != null ? `+${margin2023.toFixed(0)}pp` : "n/a"}`} />}
        </Stack>
      </Stack>
    </Stack>
  </Box>
);

// Page-2 policy table: Policy | Net Support (NZ) [national, muted] | Net Support [local, primary].
// Values are pp/percentages already 0–100 (or a signed net) — rendered AS-IS with a "%" suffix.
// Value+bar primitive shared across page 2 (mirrors page 1's #5 / softness / overlap bar
// language). mode="share": 0–100 left-origin fill. mode="net": diverging bar centred at 0,
// positive fills right (POS), negative fills left (NEG) — handles negative net support
// cleanly, not assumed positive. Local fill is prominent; national shows as a muted tick.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Diverging net-support axis half-range: net is calibrated to ±NET_DOMAIN (not ±100) so the
// realistic net range fills the track's half rather than leaving it mostly empty. Clamped, so
// a beyond-range value pins to the end (exact figure is always shown numerically alongside).
const NET_DOMAIN = 90;
const StatBar = ({ local, national, mode = "share", color = TEAL }) => {
  const pos = (local ?? 0) >= 0;
  let fill = null, natLeft = null;
  if (mode === "net") {
    fill = local != null ? { w: clamp(Math.abs(local) / NET_DOMAIN * 50, 0, 50), bg: pos ? POS : NEG, side: pos ? { left: "50%" } : { right: "50%" } } : null;
    natLeft = national != null ? clamp(50 + national / NET_DOMAIN * 50, 0, 100) : null;
  } else {
    fill = local != null ? { w: clamp(local, 0, 100), bg: color, side: { left: 0 } } : null;
    natLeft = national != null ? clamp(national, 0, 100) : null;
  }
  return (
    <Box sx={{ position: "relative", flex: 1, height: 14, mx: "6px" }}>
      <Box sx={{ position: "absolute", inset: 0, bgcolor: "#d7dfe9", borderRadius: "3px", overflow: "hidden" }}>
        {mode === "net" && <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: "#aeb9c7" }} />}
        {fill && <Box sx={{ position: "absolute", top: 0, height: "100%", width: `${fill.w}%`, bgcolor: fill.bg, borderRadius: "3px", ...fill.side }} />}
      </Box>
      {natLeft != null && <Box sx={{ position: "absolute", top: "-2px", height: "18px", width: "2.5px", bgcolor: INK2, borderRadius: 1, left: `${natLeft}%`, transform: "translateX(-1px)" }} />}
    </Box>
  );
};

// Page-2 policy card (coalition net / opposition concern). ONE shared column spec (POLICY_COLS)
// drives BOTH the header and every data row — identical left label width, identical bar flex+mx,
// and identical fixed-width right cluster [ local | NZ | Δ(▲/▼ pp) ]. The header has a cell for
// ALL THREE right columns (incl. a "vs NZ" label over the delta column) so it spans the same
// three columns the rows do — that's what stops the header drifting left. Every column is
// right-aligned in header AND values so they line up. Local bold/primary; national muted; the
// arrow (not the number) carries the POS/NEG colour and is a literal local-vs-national indicator.
const POLICY_COLS = { policy: 96, local: 48, nz: 40, delta: 50 };
const policyHeadSx = { fontSize: 6.5, fontWeight: 800, color: INK3, textTransform: "uppercase", letterSpacing: "0.05em" };
// Page-1 issues table — one shared column spec drives header + rows (rank | name | bar | NZ |
// Local | Δ), so the delta column added here can't reintroduce header drift.
const ISSUE_COLS = { rank: 18, name: 82, nz: 40, local: 48, delta: 50 };
const issueHeadSx = { fontSize: 6.5, fontWeight: 800, color: INK3, textTransform: "uppercase", letterSpacing: "0.06em" };
const PolicyBandCard = ({ eyebrow, rows, accent = TEAL, mode = "share" }) => {
  const fmt = (v) => (v == null ? "n/a" : `${mode === "net" && v > 0 ? "+" : ""}${v.toFixed(0)}%`);
  return (
    <AccentCard accent={accent} eyebrow={eyebrow} sx={{ p: "8px 11px", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" sx={{ pb: "2px", borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ width: POLICY_COLS.policy, flexShrink: 0, ...policyHeadSx }}>Policy</Typography>
        <Box sx={{ flex: 1, mx: "6px" }} />
        <Typography sx={{ width: POLICY_COLS.local, flexShrink: 0, ...policyHeadSx, textAlign: "right" }}>Local</Typography>
        <Typography sx={{ width: POLICY_COLS.nz, flexShrink: 0, ...policyHeadSx, textAlign: "right" }}>NZ</Typography>
        <Typography sx={{ width: POLICY_COLS.delta, flexShrink: 0, ...policyHeadSx, textAlign: "right" }}>vs NZ</Typography>
      </Stack>
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        {rows.map((r, i) => {
          const up = r.local != null && r.national != null && r.local > r.national;
          const down = r.local != null && r.national != null && r.local < r.national;
          const c = up ? POS : down ? NEG : INK3;
          const gap = (r.local != null && r.national != null) ? Math.abs(r.local - r.national) : null;
          const arrow = up ? "▲" : down ? "▼" : "·";
          return (
            <Stack key={r.label} direction="row" alignItems="center" sx={{ flex: 1, minHeight: 0, borderBottom: i < rows.length - 1 ? `1px solid ${SURF}` : "none" }}>
              <Typography sx={{ width: POLICY_COLS.policy, flexShrink: 0, fontSize: 9, fontWeight: 700, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "2px" }}>{r.label}</Typography>
              <StatBar local={r.local} national={r.national} mode={mode} color={accent} />
              <Typography sx={{ width: POLICY_COLS.local, flexShrink: 0, fontSize: 11, fontWeight: 800, color: INK, textAlign: "right", whiteSpace: "nowrap" }}>{fmt(r.local)}</Typography>
              <Typography sx={{ width: POLICY_COLS.nz, flexShrink: 0, fontSize: 8.5, fontWeight: 700, color: INK3, textAlign: "right", whiteSpace: "nowrap" }}>{fmt(r.national)}</Typography>
              <Box sx={{ width: POLICY_COLS.delta, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px", whiteSpace: "nowrap" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, lineHeight: 1 }}>{arrow}</Typography>
                <Typography sx={{ fontSize: 9, fontWeight: 800, color: c, whiteSpace: "nowrap" }}>{gap != null ? `${gap.toFixed(0)}pp` : ""}</Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </AccentCard>
  );
};

// Page-2 message card: family's TOP-3 by LOCAL won-share. Rows flex-fill the band (even
// rhythm, no space-between voids). Each: number badge + text, then won% bar (local prominent,
// national tick) + local% + muted NZ% + legible ▲/▼ vs-national (page-1 over/under convention).
const MessageBandCard = ({ title, texts, top3, accent = ACCENT_MAPS }) => (
  <AccentCard accent={accent} eyebrow={title} sx={{ p: "8px 10px", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {top3.map((m, i) => {
        const up = m.national != null && m.local > m.national;
        const down = m.national != null && m.local < m.national;
        const c = up ? POS : down ? NEG : INK3;
        const gap = m.national != null ? Math.abs(m.local - m.national) : null;
        const arrow = up ? "▲" : down ? "▼" : "n/a";
        return (
          <Stack key={m.n} sx={{ flex: 1, minHeight: 0, justifyContent: "center", gap: "5px", py: "4px", borderTop: i === 0 ? "none" : `1px solid ${SURF}` }}>
            <Stack direction="row" spacing={0.7} alignItems="flex-start">
              <Box sx={{ width: 15, height: 15, flexShrink: 0, borderRadius: "50%", bgcolor: mix(accent, 0.16), display: "grid", placeItems: "center", mt: "1px" }}>
                <Typography sx={{ fontSize: 8, fontWeight: 800, color: accent }}>{i + 1}</Typography>
              </Box>
              <Typography sx={{ fontSize: 8.2, color: INK, lineHeight: 1.3, flex: 1 }}>{texts[m.n]}</Typography>
            </Stack>
            {/* Fixed, non-wrapping slots so local% · NZ% · ▲pp align down and across all 3 cards.
                Number slots are flexShrink:0 + nowrap; the bar (flex) yields space, never them. */}
            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ pl: "22px" }}>
              <StatBar local={m.local} national={m.national} mode="share" color={accent} />
              <Typography sx={{ width: 32, flexShrink: 0, fontSize: 10.5, fontWeight: 800, color: INK, textAlign: "right", whiteSpace: "nowrap" }}>{m.local != null ? `${m.local.toFixed(0)}%` : "n/a"}</Typography>
              <Typography sx={{ width: 42, flexShrink: 0, fontSize: 8, color: INK3, textAlign: "right", whiteSpace: "nowrap" }}>{m.national != null ? `NZ ${m.national.toFixed(0)}%` : "NZ n/a"}</Typography>
              <Box sx={{ width: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px", whiteSpace: "nowrap" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, lineHeight: 1 }}>{arrow}</Typography>
                <Typography sx={{ fontSize: 9, fontWeight: 800, color: c, whiteSpace: "nowrap" }}>{gap != null ? `${gap.toFixed(0)}pp` : ""}</Typography>
              </Box>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  </AccentCard>
);

// Approval doughnut card (top of page 2). Nested rings: OUTER = selected constituency (local),
// INNER = NZ; each split approve (green) / disapprove (red) / don't-know (grey). Drawn as SVG
// stroke-dasharray arcs (rasterise cleanly in html2canvas), paired with an approve/disapprove/dk
// × local/NZ mini-table and a local-approve headline in the centre.
// Clockwise order (from 12 o'clock): approve (green) → don't-know (grey) → disapprove (red).
const APPROVAL_SEGS = [
  { key: "good", label: "Approve", color: POS },
  { key: "dk", label: "Don’t know", color: "#c9d1dc" },
  { key: "bad", label: "Disapprove", color: NEG },
];
const ApprovalDoughnutCard = ({ eyebrow, accent, local, national }) => {
  const size = 86, cx = size / 2, cy = size / 2;
  const rings = [
    { d: local, r: size / 2 - 6, tw: 10 },     // outer ring = local
    { d: national, r: size / 2 - 19, tw: 10 },  // inner ring = NZ
  ];
  const arcs = (d, C) => {
    const total = APPROVAL_SEGS.reduce((s, seg) => s + (num(d[seg.key]) ?? 0), 0);
    if (!total) return [];
    let acc = 0;
    return APPROVAL_SEGS.map((seg) => {
      const len = ((num(d[seg.key]) ?? 0) / total) * C;
      const node = { len, off: -acc, color: seg.color };
      acc += len;
      return node;
    });
  };
  const localApprove = num(local.good);
  return (
    <AccentCard accent={accent} eyebrow={eyebrow} sx={{ p: "8px 11px", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
            {rings.map((ring, ri) => {
              const C = 2 * Math.PI * ring.r;
              return (
                <g key={ri}>
                  <circle cx={cx} cy={cy} r={ring.r} fill="none" stroke="#eef2f7" strokeWidth={ring.tw} />
                  {arcs(ring.d, C).map((a, i) => (
                    <circle key={i} cx={cx} cy={cy} r={ring.r} fill="none" stroke={a.color} strokeWidth={ring.tw}
                      strokeDasharray={`${a.len} ${C - a.len}`} strokeDashoffset={a.off} transform={`rotate(-90 ${cx} ${cy})`} />
                  ))}
                </g>
              );
            })}
          </svg>
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: POS, lineHeight: 1 }}>{localApprove != null ? `${localApprove.toFixed(0)}%` : "n/a"}</Typography>
            <Typography sx={{ fontSize: 5.5, fontWeight: 800, color: INK3, textTransform: "uppercase", letterSpacing: "0.05em" }}>approve</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" sx={{ pb: "2px", borderBottom: `1px solid ${BORDER}` }}>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ width: 34, ...policyHeadSx, textAlign: "right" }}>Local</Typography>
            <Typography sx={{ width: 28, ...policyHeadSx, textAlign: "right" }}>NZ</Typography>
            <Typography sx={{ width: 42, ...policyHeadSx, textAlign: "right" }}>vs NZ</Typography>
          </Stack>
          {APPROVAL_SEGS.map((seg) => {
            const lv = num(local[seg.key]), nv = num(national[seg.key]);
            const up = lv != null && nv != null && lv > nv;
            const down = lv != null && nv != null && lv < nv;
            const c = up ? POS : down ? NEG : INK3;
            const gap = (lv != null && nv != null) ? Math.abs(lv - nv) : null;
            const arrow = up ? "▲" : down ? "▼" : "·";
            return (
              <Stack key={seg.key} direction="row" alignItems="center" sx={{ py: "2px" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: seg.color, flexShrink: 0, mr: 0.6 }} />
                <Typography sx={{ flex: 1, fontSize: 8.5, fontWeight: 600, color: INK, whiteSpace: "nowrap" }}>{seg.label}</Typography>
                <Typography sx={{ width: 34, fontSize: 10, fontWeight: 800, color: INK, textAlign: "right" }}>{lv != null ? `${lv.toFixed(0)}%` : "n/a"}</Typography>
                <Typography sx={{ width: 28, fontSize: 8.5, fontWeight: 700, color: INK3, textAlign: "right" }}>{nv != null ? `${nv.toFixed(0)}%` : "n/a"}</Typography>
                <Box sx={{ width: 42, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px", whiteSpace: "nowrap" }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: c, lineHeight: 1 }}>{arrow}</Typography>
                  <Typography sx={{ fontSize: 8, fontWeight: 800, color: c }}>{gap != null ? `${gap.toFixed(0)}pp` : ""}</Typography>
                </Box>
              </Stack>
            );
          })}
          <Typography sx={{ fontSize: 6.5, color: INK3, mt: "2px" }}>Outer ring = electorate · inner = NZ</Typography>
        </Box>
      </Stack>
    </AccentCard>
  );
};

const ConstituencyReportView = ({ area, focal = "nat", SA2Map, constituencyMap }) => {
  const [d, setD] = useState(null);
  // Render-complete signal (shared convention with the Chromium render route): flips true
  // once the three fetches have resolved (d set) AND the DOM has had a rAF + short delay to
  // let Recharts/SVG actually paint. Exposed as data-render-complete on the page-1 node; the
  // exporter gates the first capture on it so we never snapshot a half-drawn page.
  const [ready, setReady] = useState(false);
  const areaName = area?.area_name || "n/a";
  const focalParty = PARTIES[focal] || PARTIES.nat;

  useEffect(() => {
    if (!area?.area_id) return;
    let alive = true;
    setD(null);
    (async () => {
      const constCode = area.area_id;
      const allKeys = [...PRED_KEYS, ...PV_DENOM_KEYS, ...CONSIDER_KEYS, ...ISSUE_KEYS, ...SEG_KEYS, ...PAGE2_KEYS];
      const sa2Keys = [...PV_KEYS, ...PRED_KEYS, ...OVERLAP_KEYS];
      try {
        // No single wave fills the report: wave 2 carried the policy/message batteries,
        // wave 3 added budget/trust/overlap and refreshed VI/issues, wave 4 refreshed
        // those again without the message batteries. So fetch every wave and merge
        // oldest to newest, a later non-null winning — each slot shows its most recent
        // real figure and falls back as far as it has to.
        //
        // This needs no per-battery special-casing. A question absent from a wave comes
        // back NULL (the mirror is read with union_by_name, so the column exists across
        // the union and is simply null for the waves that lack it), and null never
        // overwrites an earlier wave's value. Adding a wave means adding it to
        // constants/waves.js and nothing here.
        const ordered = [...WAVES].sort((a, b) => a - b);
        const [grpByWave, sa2ByWave] = await Promise.all([
          Promise.all(ordered.map((w) =>
            loadGroupData({ groupKeys: allKeys, constCode, wave: w }))),
          Promise.all(ordered.map((w) =>
            loadRows({ groupKeys: sa2Keys, constCode, level: "SA2", wave: w }))),
        ]);
        if (!alive) return;
        const map = {};
        grpByWave.forEach((rows) => {
          (rows || []).forEach((r) => {
            const cur = map[r.key] || { c: null, n: null };
            map[r.key] = { c: num(r.constituency) ?? cur.c, n: num(r.national) ?? cur.n };
          });
        });
        setD({ map, sa2: mergeSa2(...sa2ByWave) });
      } catch (e) {
        console.error("[ConstituencyReportView] load failed:", e);
        if (alive) setD({ map: {}, sa2: [] });
      }
    })();
    return () => { alive = false; };
  }, [area?.area_id]);

  // Render-complete guard. Reset the moment a fetch starts (d -> null); once data lands, let
  // the browser paint (double rAF) then a short settle for Recharts before signalling ready.
  useEffect(() => {
    if (!d) { setReady(false); return; }
    let raf2, t;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => { t = setTimeout(() => setReady(true), 80); });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(t); };
  }, [d]);

  const map = d?.map || {};
  const rank = (getVal) => RP.map((p) => ({ ...p, v: getVal(p) })).filter((p) => p.v != null).sort((a, b) => b.v - a.v);
  // 2023 baseline correction — re-base past_vote_* onto the voters-only denominator
  // (Σ over all 7 party past-votes = 1 − non-voter share) so it is comparable to VI and
  // matches official party-vote share. Applied to the header figure and #5 deltas.
  const pvDenom = PV_DENOM_KEYS.reduce((s, k) => s + (num(map[k]?.c) ?? 0), 0);
  const corr2023 = (pvField) => (pvDenom > 0 ? (100 * (num(map[pvField]?.c) ?? 0)) / pvDenom : null);
  const winner2023 = RP.map((p) => ({ ...p, v: corr2023(p.pastVoteField) })).filter((p) => p.v != null).sort((a, b) => b.v - a.v);
  const w23 = winner2023[0] || null;
  const margin2023 = winner2023.length >= 2 ? winner2023[0].v - winner2023[1].v : null;
  const projected = rank((p) => map[p.field]?.c);
  const proj = projected[0] || null;
  // #5 (projected VI) & #6 (softness): six parties ranked by projected VI, top-N shown flat.
  const barParties = BAR_PARTIES
    .map((p) => ({ ...p, _vi: num(map[p.field]?.c) ?? 0 }))
    .sort((a, b) => b._vi - a._vi)
    .slice(0, BAR_N);

  // ── Top issues — concern % shown as value+bar (was ordinal rank/▲▼) ──────────────────
  // Real data: the SAME local (c) / national (n) issue-concern values from the loadGroupData
  // aggregate — no new fetch. Top 5 by LOCAL concern desc; deterministic tie-break on issue
  // field-key alpha (equal concern never reorders between renders).
  //
  // ×3 DISPLAY MULTIPLIER: _issue_n is the pick-share of a SELECT-3 question (sums to ~300%
  // across issues), so a single issue's raw share reads low. We triple ONLY the rendered value
  // (stored/aggregate values untouched). Respondents may pick <3, so in practice nothing hits
  // 100% tripled — GUARD below clamps the BAR at 100 (never the shown number) if any does.
  const ISSUE_MULT = 3;
  const byLocal = [...ISSUE_KEYS]
    .map((k) => ({ key: k, name: ISSUE_INFO[k].name, local: num(map[k]?.c) ?? 0, national: num(map[k]?.n) ?? 0 }))
    .sort((a, b) => (b.local - a.local) || a.key.localeCompare(b.key));
  const topIssues = byLocal.slice(0, 5).map((r, i) => ({
    ...r, localRank: i + 1, localPct: r.local * ISSUE_MULT, nationalPct: r.national * ISSUE_MULT,
  }));
  topIssues.forEach((it) => {
    if (it.localPct > 100 || it.nationalPct > 100) {
      console.warn(`[ConstituencyReportView] issue "${it.name}" exceeds 100% after ×${ISSUE_MULT} (local ${it.localPct.toFixed(0)}, nat ${it.nationalPct.toFixed(0)}) — bar clamped, true value shown`);
    }
  });

  const sa2Rows = useMemo(() => d?.sa2 || [], [d]);
  const sa2ById = useMemo(() => { const m = new Map(); sa2Rows.forEach((r) => m.set(String(r.area_id), r)); return m; }, [sa2Rows]);
  const sa2Features = useMemo(() => {
    const feats = SA2Map?.features || [];
    if (!feats.length || !sa2ById.size) return [];
    const inElectorate = feats.filter((f) => sa2ById.has(String(f.properties?.area_id)));
    // Mainland-only for the report maps: dropping island SA2s here recomputes the extent
    // (SVGChoropleth fits its viewBox to whatever features it receives), undistorting the
    // mainland. Applied to the shared set so all report maps stay identical in coverage.
    const mainland = inElectorate.filter((f) => !isIslandFeature(f));
    // Guard: an all-island electorate would otherwise render blank — keep the full set.
    return mainland.length ? mainland : inElectorate;
  }, [SA2Map, sa2ById]);
  // Same-origin vector "basemap": all electorate polygons, drawn faint-grey behind each
  // choropleth for geographic context (SVGChoropleth clips to the neighbouring ones).
  const basemapFeatures = useMemo(() => constituencyMap?.features || [], [constituencyMap]);

  const winnerBy = (row, keysObj) => { let best = null; keysObj.forEach((p) => { const v = num(row?.[p.valueKey]); if (v != null && (best == null || v > best.v)) best = { v, color: p.color, key: p.key }; }); return best; };
  const pvCols = RP.map((p) => ({ color: p.color, key: p.key, valueKey: p.pastVoteField }));
  const predCols = RP.map((p) => ({ color: p.color, key: p.key, valueKey: p.field }));
  // #3 winner is argmax of past_vote within a cell — the voters-base correction divides every
  // party by the same per-cell denominator, so the winner (and thus fill) is invariant. Raw is fine.
  const fill2023 = (id) => winnerBy(sa2ById.get(String(id)), pvCols)?.color || "#eef2f7";
  const fillProj = (id) => winnerBy(sa2ById.get(String(id)), predCols)?.color || "#eef2f7";
  // Changed SA2s (projected winner ≠ 2023) get a diagonal hatch in the winner's colour, over a
  // light tint of it (a same-colour hatch on a solid same-colour fill would be invisible).
  // Winner argmax is base-invariant to the 2023 correction.
  const hatchFor = (id) => {
    const row = sa2ById.get(String(id));
    const a = winnerBy(row, pvCols)?.key;
    const b = winnerBy(row, predCols);
    return a && b?.key && a !== b.key ? { line: b.color, bg: mix(b.color, 0.20) } : null;
  };
  // ── Overlap between the FOCAL coalition party and opposition (both rated ≥6) ───────────
  // Real metric {focal}_overlap_n — electorate stat (map[…].c) + per-SA2 (sa2ById) for the
  // concentration map & top-SA2 table. Focal-driven, so it re-reads on the focal toggle without
  // a refetch (all three overlap columns are already fetched). Mainland-only sa2Features shared
  // with the winner maps so map + table describe the same SA2s.
  const focalOverlapField = `${focal}_overlap_n`;
  const overlapPct = num(map[focalOverlapField]?.c);
  const overlapRankedSA2 = useMemo(() => (
    sa2Features
      .map((f) => ({
        id: String(f.properties?.area_id ?? ""),
        name: f.properties?.area_name || String(f.properties?.area_id ?? ""),
        v: num(sa2ById.get(String(f.properties?.area_id))?.[focalOverlapField]),
      }))
      .filter((r) => r.v != null)
      .sort((a, b) => b.v - a.v)
  ), [sa2Features, sa2ById, focalOverlapField]);
  // Rank-based (quantile) colour fraction — spreads the ramp EVENLY across the in-view mainland
  // SA2s so clustered values still read as distinct (higher contrast than scaling raw value over
  // min→max). Highest overlap → 1 (darkest), lowest → 0 (lightest).
  const overlapRankFrac = useMemo(() => {
    const m = new Map();
    const n = overlapRankedSA2.length;
    overlapRankedSA2.forEach((r, i) => m.set(r.id, n > 1 ? 1 - i / (n - 1) : 1));
    return m;
  }, [overlapRankedSA2]);
  const fillOverlap = (id) => {
    const t = overlapRankFrac.get(String(id));
    if (t == null) return "#eef2f7";
    return mix2(OVERLAP_LO, OVERLAP_HI, t);
  };

  // ── PAGE 2 data (all fields already 0–100 %; consumed AS-IS — no ×10 / ×100) ──────────
  // Coalition net support = support_vi_n − oppose_vi_n (pp; may be negative — not clamped).
  const net = (stem, lvl) => {
    const s = num(map[`${stem}_support_vi_n`]?.[lvl]);
    if (s == null) return null;
    return s - (num(map[`${stem}_oppose_vi_n`]?.[lvl]) ?? 0);
  };
  const coalitionRows = COALITION_POLICIES.map((p) => ({ label: p.label, local: net(p.stem, "c"), national: net(p.stem, "n") }));
  const oppRows = (list) => list.map((p) => ({ label: p.label, local: num(map[p.field]?.c), national: num(map[p.field]?.n) }));
  // Budget '26 net approval per item = good − bad (mirrors coalition net = support − oppose).
  const budgetNet = (stem, lvl) => {
    const g = num(map[`vi_${stem}_good_n`]?.[lvl]);
    if (g == null) return null;
    return g - (num(map[`vi_${stem}_bad_n`]?.[lvl]) ?? 0);
  };
  const budgetRows = BUDGET_ITEMS.map((p) => ({ label: p.label, local: budgetNet(p.stem, "c"), national: budgetNet(p.stem, "n") }));
  // Approval doughnuts (approve / disapprove / don't-know), local (c) + NZ (n).
  const approvalData = (goodK, badK, dkK) => ({
    local: { good: num(map[goodK]?.c), bad: num(map[badK]?.c), dk: num(map[dkK]?.c) },
    national: { good: num(map[goodK]?.n), bad: num(map[badK]?.n), dk: num(map[dkK]?.n) },
  });
  const govApproval = approvalData("gov_good_n", "gov_bad_n", "gov_dn_n");
  const budgetApproval = approvalData("vi_budget_good_n", "vi_budget_bad_n", "vi_budget_dk_n");
  // Trust box — aligned to the top-5 issues. For each, coalition / opposition / neither trust
  // (electorate-level). Issues with no trust field (e.g. education) render as no-data.
  const trustRows = topIssues.map((it) => {
    const stem = ISSUE_TO_TRUST[it.key];
    if (!stem) return { key: it.key, name: it.name, hasData: false };
    const g = (suf, lvl) => num(map[`vi_${stem}_${suf}_n`]?.[lvl]);
    const coalition = g("coalition", "c"), opposition = g("opposition", "c"), none = g("none", "c");
    return {
      key: it.key, name: it.name, coalition, opposition, none,
      coalitionN: g("coalition", "n"), oppositionN: g("opposition", "n"), noneN: g("none", "n"),
      hasData: coalition != null || opposition != null,
    };
  });
  // Top-3 messages per family BY LOCAL won-share, recomputed every render (which msgs surface
  // varies by seat). Deterministic tie-break: equal local share → lower msg number first.
  const msgTop3 = (family) => Array.from({ length: 6 }, (_, i) => {
    const n = i + 1, key = `${family}_msg${n}_won_n`;
    return { n, local: num(map[key]?.c), national: num(map[key]?.n) };
  }).filter((m) => m.local != null).sort((a, b) => (b.local - a.local) || (a.n - b.n)).slice(0, 3);

  // Two explicit page nodes, each a fixed A4 tile (794×1123, overflow:hidden) captured to its
  // own PDF page. Papers are the preview affordance only; the exporter captures the inner
  // #report-content-{id}-pN nodes, so the Paper chrome never reaches the PDF.
  const pageWrapSx = { borderRadius: 2, overflow: "hidden" };
  const bannerProps = { areaName, focalParty, proj, w23, margin2023 };

  return (
    <Stack spacing={3} alignItems="center" sx={{ width: 794 }}>
    {/* ── PAGE 1 — existing report body (unchanged this step) ─────────────── */}
    <Paper elevation={3} sx={pageWrapSx}>
    {!d ? (
      <Box
        id={`report-content-${area?.area_id}-p1`}
        data-render-complete="false"
        sx={{ width: "794px", height: "1123px", bgcolor: "#fff", display: "grid", placeItems: "center" }}
      >
        <CircularProgress size={28} />
      </Box>
    ) : (
    <Box
      id={`report-content-${area?.area_id}-p1`}
      data-render-complete={ready ? "true" : "false"}
      sx={{
        width: "794px", height: "1123px", overflow: "hidden", bgcolor: "#fff", color: INK,
        p: "24px 28px", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "10px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* Header — shared banner (identical on page 2). */}
      <ReportBanner {...bannerProps} />

      {/* SA2 maps (faint vector basemap behind) with a compact inline legend */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <AccentCard accent={ACCENT_MAPS} eyebrow="2023 winner by SA2" sx={{ p: "8px 8px 4px" }}>
            <SVGChoropleth features={sa2Features} basemapFeatures={basemapFeatures} fillFor={fill2023} width={330} height={196} />
          </AccentCard>
          <AccentCard accent={ACCENT_MAPS} eyebrow="Projected winner · Δ vs 2023" sx={{ p: "8px 8px 4px" }}>
            <SVGChoropleth features={sa2Features} basemapFeatures={basemapFeatures} fillFor={fillProj} hatchFor={hatchFor} idPrefix="proj" width={330} height={196} />
            <Typography sx={{ fontSize: 7, color: INK2 }}>Hatched fill = SA2 where the projected winner differs from 2023.</Typography>
          </AccentCard>
        </Box>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ mt: "5px" }}>
          <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>Winner</Typography>
          {RP.map((p) => (
            <Stack key={p.key} direction="row" alignItems="center" spacing={0.35}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: p.color }} />
              <Typography sx={{ fontSize: 7.5, color: INK2, fontWeight: 600 }}>{p.shortLabel}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Projected vote & change | Softness */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <AccentCard accent={ACCENT_VOTE} eyebrow="Projected vote & change from 2023">
          {barParties.map((p) => {
            const vi = num(map[p.field]?.c) ?? 0;
            const delta = vi - (corr2023(p.pastVoteField) ?? 0); // vs corrected voters-base 2023
            return (
              <Stack key={p.key} direction="row" alignItems="center" spacing={0.75} sx={{ mb: "4px" }}>
                <Box sx={{ width: 40, flexShrink: 0, display: "flex", justifyContent: "flex-start" }}><Typography sx={{ fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", color: p.key === "act" ? "#B8860B" : p.color }}>{p.shortLabel}</Typography></Box>
                <Box sx={{ flex: 1, height: 11, bgcolor: "#f1f5f9", borderRadius: "3px", overflow: "hidden" }}>
                  <Box sx={{ width: `${Math.min(100, vi * 1.7)}%`, height: "100%", bgcolor: p.color }} />
                </Box>
                <Typography sx={{ fontSize: 9.5, width: 34, textAlign: "right", fontWeight: 800 }}>{vi.toFixed(0)}%</Typography>
                <Delta v={delta} />
              </Stack>
            );
          })}
        </AccentCard>

        <AccentCard accent={ACCENT_VOTE} eyebrow="Softness of vote">
          {barParties.map((p) => {
            const consider = (num(map[`${p.key}_consider`]?.c) ?? 0) / 10;
            const vote = num(map[p.field]?.c) ?? 0;
            const soft = Math.max(0, consider - vote);
            return (
              <Stack key={p.key} direction="row" alignItems="center" spacing={0.75} sx={{ mb: "4px" }}>
                <Box sx={{ width: 40, flexShrink: 0, display: "flex", justifyContent: "flex-start" }}><Typography sx={{ fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", color: p.key === "act" ? "#B8860B" : p.color }}>{p.shortLabel}</Typography></Box>
                <Box sx={{ position: "relative", flex: 1, height: 11, bgcolor: "#f1f5f9", borderRadius: "3px", overflow: "hidden" }}>
                  <Box sx={{ position: "absolute", left: 0, height: "100%", width: `${Math.min(100, consider)}%`, bgcolor: mix(p.color, 0.32) }} />
                  <Box sx={{ position: "absolute", left: 0, height: "100%", width: `${Math.min(100, vote)}%`, bgcolor: p.color }} />
                </Box>
                <Typography sx={{ fontSize: 9, width: 64, textAlign: "right", color: INK2 }}>soft <b style={{ color: INK }}>{soft.toFixed(0)}%</b></Typography>
              </Stack>
            );
          })}
          <Typography sx={{ fontSize: 7, color: INK2, mt: "3px" }}>Solid = projected vote · light = persuadable headroom (consideration − vote).</Typography>
        </AccentCard>
      </Box>

      {/* LOWER-REGION WRAPPER — tightens the VERTICAL inter-card gaps below the issues/trust
          row (issues↔grid, and the in-column A↔B / C↔D gaps) to a uniform 6px WITHOUT touching
          the upper region's 10px page-column gaps. flex:1 keeps the whole stack bottom-aligned
          to the page edge; the reclaimed space grows the overlap map (B) and top-SA2 table (C). */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
      {/* Top issues — ranking table (left) + trust holding box (right); 161px fixed row. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", height: 161 }}>
        {/* LEFT — top-5 issues by local concern; concern % (×3) as value+bar, bold-local/muted-nat */}
        <AccentCard accent={ACCENT_ISSUES} eyebrow="Top issues · concern % (local vs national)" sx={{ p: "8px 11px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Stack direction="row" alignItems="center" sx={{ pb: "2px", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ width: ISSUE_COLS.rank, flexShrink: 0, ...issueHeadSx }}>#</Typography>
            <Typography sx={{ width: ISSUE_COLS.name, flexShrink: 0, ...issueHeadSx }}>Issue</Typography>
            <Box sx={{ flex: 1, mx: "6px" }} />
            <Typography sx={{ width: ISSUE_COLS.nz, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>NZ</Typography>
            <Typography sx={{ width: ISSUE_COLS.local, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>Local</Typography>
            <Typography sx={{ width: ISSUE_COLS.delta, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>vs NZ</Typography>
          </Stack>
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {topIssues.map((it, i) => {
              // Δ on the DISPLAYED (×3) figures so the arrow reconciles with the shown local/NZ.
              const up = it.localPct > it.nationalPct, down = it.localPct < it.nationalPct;
              const c = up ? POS : down ? NEG : INK3;
              const gap = Math.abs(it.localPct - it.nationalPct);
              const arrow = up ? "▲" : down ? "▼" : "·";
              return (
                <Stack key={it.key} direction="row" alignItems="center" sx={{ flex: 1, minHeight: 0, borderBottom: i < topIssues.length - 1 ? `1px solid ${SURF}` : "none" }}>
                  <Box sx={{ width: ISSUE_COLS.rank, flexShrink: 0 }}>
                    <Box sx={{ width: 15, height: 15, borderRadius: "50%", bgcolor: SURF, border: `1px solid ${BORDER}`, display: "grid", placeItems: "center" }}>
                      <Typography sx={{ fontSize: 8, fontWeight: 800, color: INK2 }}>{it.localRank}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ width: ISSUE_COLS.name, flexShrink: 0, fontSize: 9, fontWeight: 700, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "2px" }}>{it.name}</Typography>
                  <StatBar local={it.localPct} national={it.nationalPct} mode="share" color={ACCENT_ISSUES} />
                  <Typography sx={{ width: ISSUE_COLS.nz, flexShrink: 0, fontSize: 8.5, fontWeight: 700, color: INK3, textAlign: "right", whiteSpace: "nowrap" }}>{it.nationalPct.toFixed(0)}%</Typography>
                  <Typography sx={{ width: ISSUE_COLS.local, flexShrink: 0, fontSize: 11, fontWeight: 800, color: INK, textAlign: "right", whiteSpace: "nowrap" }}>{it.localPct.toFixed(0)}%</Typography>
                  <Box sx={{ width: ISSUE_COLS.delta, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px", whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, lineHeight: 1 }}>{arrow}</Typography>
                    <Typography sx={{ fontSize: 9, fontWeight: 800, color: c, whiteSpace: "nowrap" }}>{gap.toFixed(0)}pp</Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </AccentCard>

        {/* RIGHT — Government-vs-Opposition trust, aligned to the top-5 issues at left. Per issue:
             stacked bar (coalition | neither | opposition) + the coalition & opposition headline %s. */}
        <AccentCard accent={ACCENT_ISSUES} eyebrow="Government vs opposition trust · by issue" sx={{ p: "8px 11px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: "3px", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 6, fontWeight: 700, color: INK3, textTransform: "uppercase", letterSpacing: "0.03em" }}>Upper = electorate · lower = NZ</Typography>
            <Box sx={{ flex: 1 }} />
            {[["Coalition", TRUST_COAL], ["Neither", TRUST_NONE], ["Opposition", TRUST_OPP]].map(([lbl, col]) => (
              <Stack key={lbl} direction="row" alignItems="center" spacing={0.3}>
                <Box sx={{ width: 7, height: 7, borderRadius: "2px", bgcolor: col }} />
                <Typography sx={{ fontSize: 6.5, fontWeight: 800, color: INK3, textTransform: "uppercase", letterSpacing: "0.04em" }}>{lbl}</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {trustRows.map((t, i) => (
              <Stack key={t.key} direction="row" alignItems="center" sx={{ flex: 1, minHeight: 0, borderBottom: i < trustRows.length - 1 ? `1px solid ${SURF}` : "none" }}>
                <Typography sx={{ width: 62, flexShrink: 0, fontSize: 9, fontWeight: 700, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "4px" }}>{t.name}</Typography>
                {t.hasData ? (
                  <>
                    {/* paired stacked bars — upper = electorate (local), lower = NZ (muted reference) */}
                    <Stack sx={{ flex: 1, mx: "6px", gap: "1.5px" }}>
                      <Box sx={{ height: 8, borderRadius: "2px", overflow: "hidden", display: "flex", bgcolor: "#eef2f7" }}>
                        <Box sx={{ width: `${clamp(t.coalition ?? 0, 0, 100)}%`, bgcolor: TRUST_COAL }} />
                        <Box sx={{ width: `${clamp(t.none ?? 0, 0, 100)}%`, bgcolor: TRUST_NONE }} />
                        <Box sx={{ width: `${clamp(t.opposition ?? 0, 0, 100)}%`, bgcolor: TRUST_OPP }} />
                      </Box>
                      <Box sx={{ height: 5, borderRadius: "2px", overflow: "hidden", display: "flex", bgcolor: "#eef2f7" }}>
                        <Box sx={{ width: `${clamp(t.coalitionN ?? 0, 0, 100)}%`, bgcolor: mix(TRUST_COAL, 0.55) }} />
                        <Box sx={{ width: `${clamp(t.noneN ?? 0, 0, 100)}%`, bgcolor: mix(TRUST_NONE, 0.55) }} />
                        <Box sx={{ width: `${clamp(t.oppositionN ?? 0, 0, 100)}%`, bgcolor: mix(TRUST_OPP, 0.55) }} />
                      </Box>
                    </Stack>
                    <Typography sx={{ width: 30, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: TRUST_COAL, textAlign: "right", whiteSpace: "nowrap" }}>{t.coalition != null ? `${t.coalition.toFixed(0)}%` : "n/a"}</Typography>
                    <Typography sx={{ width: 30, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: TRUST_OPP, textAlign: "right", whiteSpace: "nowrap" }}>{t.opposition != null ? `${t.opposition.toFixed(0)}%` : "n/a"}</Typography>
                  </>
                ) : (
                  <Typography sx={{ flex: 1, fontSize: 7.5, color: INK3, fontStyle: "italic", textAlign: "center" }}>no trust data for this issue</Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </AccentCard>
      </Box>

      {/* Lower region — "overlap voters" scaffold (PLACEHOLDER). Two columns, each stacking a
          fixed-height top box over a flex-fill bottom box, so both columns bottom-align at the
          page edge. Replaces the retired vote-overlap / biggest-segment / concentration / attack-
          frame elements. All three overlap elements read the SAME stand-in column. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", flex: 1, minHeight: 0 }}>
        {/* LEFT column: (A) overlap stat  →  (B) mainland-only concentration map */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minHeight: 0 }}>
          {/* A — small stat (height ≤ 2/3 of the 161px issues card) */}
          <AccentCard accent={ACCENT_OVERLAP} eyebrow={`Vote overlap · ${focalParty.shortLabel} × opposition`} sx={{ height: 104, p: "8px 11px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* eyebrow pins to the top (AccentCard); the stat + caption centre in the space below */}
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: ACCENT_OVERLAP, lineHeight: 1 }}>{overlapPct != null ? `${overlapPct.toFixed(0)}%` : "n/a"}</Typography>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: INK2, lineHeight: 1.2 }}>rate {focalParty.name} <b>and</b> an opposition party ≥6/10</Typography>
              </Stack>
              <Typography sx={{ fontSize: 8.5, color: INK3, mt: "6px", lineHeight: 1.3 }}>
                Persuadable overlap: voters open to both {focalParty.name} and the opposition bloc.
              </Typography>
            </Box>
          </AccentCard>
          {/* B — concentration map, fills to the page bottom; mainland-only via sa2Features */}
          <AccentCard accent={ACCENT_OVERLAP} eyebrow="Overlap concentration by SA2" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, overflow: "hidden" }}>
              <SVGChoropleth features={sa2Features} basemapFeatures={basemapFeatures} fillFor={fillOverlap} width={318} height={240} />
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="center">
              <Typography sx={{ fontSize: 7.5, color: INK3 }}>low</Typography>
              <Box sx={{ width: 100, height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${OVERLAP_LO}, ${OVERLAP_HI})` }} />
              <Typography sx={{ fontSize: 7.5, color: INK3 }}>high</Typography>
            </Stack>
          </AccentCard>
        </Box>

        {/* RIGHT column: (C) top-SA2 table — now fills the whole column (Left-wing box removed),
             bottom-aligning with the map. Columns: # | SA2 | overlap | {focal} 2023 | {focal} VI.
             Shared SA2_COLS spec keeps header over columns; top-5 rows bolded + tinted. */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <AccentCard accent={ACCENT_OVERLAP} eyebrow={`Top SA2s by overlap · ${focalParty.shortLabel} 2023 & VI`} sx={{ flex: 1, minHeight: 0, p: "8px 11px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Stack direction="row" alignItems="center" sx={{ pb: "2px", borderBottom: `1px solid ${BORDER}` }}>
              <Typography sx={{ width: SA2_COLS.rank, flexShrink: 0, ...issueHeadSx }}>#</Typography>
              <Typography sx={{ flex: 1, minWidth: 0, ...issueHeadSx }}>SA2</Typography>
              <Typography sx={{ width: SA2_COLS.overlap, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>Ovlp</Typography>
              <Typography sx={{ width: SA2_COLS.y2023, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>{focalParty.shortLabel} ’23</Typography>
              <Typography sx={{ width: SA2_COLS.vi, flexShrink: 0, ...issueHeadSx, textAlign: "right" }}>{focalParty.shortLabel} VI</Typography>
            </Stack>
            <Stack sx={{ flex: 1, minHeight: 0 }}>
              {overlapRankedSA2.slice(0, 16).map((r, i) => {
                const top5 = i < 5;
                const row = sa2ById.get(r.id);
                const pv = num(row?.[focalParty.pastVoteField]);
                const vi = num(row?.[focalParty.field]);
                return (
                  <Stack key={r.id} direction="row" alignItems="center" sx={{ flex: 1, minHeight: 0, px: "2px", borderBottom: i < 15 ? `1px solid ${SURF}` : "none", bgcolor: top5 ? mix(ACCENT_OVERLAP, 0.07) : "transparent" }}>
                    <Typography sx={{ width: SA2_COLS.rank, flexShrink: 0, fontSize: 8, fontWeight: 800, color: top5 ? ACCENT_OVERLAP : INK3 }}>{i + 1}</Typography>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 8.5, fontWeight: top5 ? 800 : 600, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "4px" }}>{r.name}</Typography>
                    <Typography sx={{ width: SA2_COLS.overlap, flexShrink: 0, fontSize: 8.5, fontWeight: top5 ? 800 : 700, color: INK, textAlign: "right", whiteSpace: "nowrap" }}>{r.v.toFixed(0)}%</Typography>
                    <Typography sx={{ width: SA2_COLS.y2023, flexShrink: 0, fontSize: 8.5, fontWeight: top5 ? 700 : 600, color: INK3, textAlign: "right", whiteSpace: "nowrap" }}>{pv != null ? `${pv.toFixed(0)}%` : "n/a"}</Typography>
                    <Typography sx={{ width: SA2_COLS.vi, flexShrink: 0, fontSize: 8.5, fontWeight: top5 ? 800 : 700, color: focalParty.color, textAlign: "right", whiteSpace: "nowrap" }}>{vi != null ? `${vi.toFixed(0)}%` : "n/a"}</Typography>
                  </Stack>
                );
              })}
            </Stack>
            <Typography sx={{ fontSize: 7, color: INK3, pt: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Overlap = % rating {focalParty.shortLabel} &amp; an opposition party ≥6 · mainland SA2s · top-5 bolded.</Typography>
          </AccentCard>
        </Box>
      </Box>
      </Box>
    </Box>
    )}
    </Paper>

    {/* ── PAGE 2 — coalition policies · opposition policies · message testing ───────── */}
    <Paper elevation={3} sx={pageWrapSx}>
    {!d ? (
      <Box
        id={`report-content-${area?.area_id}-p2`}
        data-render-complete="false"
        sx={{ width: "794px", height: "1123px", bgcolor: "#fff", display: "grid", placeItems: "center" }}
      >
        <CircularProgress size={28} />
      </Box>
    ) : (
    <Box
      id={`report-content-${area?.area_id}-p2`}
      data-render-complete={ready ? "true" : "false"}
      sx={{
        width: "794px", height: "1123px", overflow: "hidden", bgcolor: "#fff", color: INK,
        p: "24px 28px", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "10px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* Shared banner — identical to page 1. */}
      <ReportBanner {...bannerProps} />

      {/* Approval doughnuts — government (left) & budget (right); outer ring = electorate, inner = NZ. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", height: 116 }}>
        <ApprovalDoughnutCard eyebrow="Government approval" accent={ACCENT_VOTE} local={govApproval.local} national={govApproval.national} />
        <ApprovalDoughnutCard eyebrow="Budget '26 approval" accent={ACCENT_VOTE} local={budgetApproval.local} national={budgetApproval.national} />
      </Box>

      {/* BAND 1 — Coalition policies (net support) | Budget '26 item approval (net, matching card) */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", flex: 1, minHeight: 0 }}>
        <PolicyBandCard eyebrow="Coalition policies · net support" rows={coalitionRows} accent={ACCENT_VOTE} mode="net" />
        <PolicyBandCard eyebrow="Budget '26 · net vote impact" rows={budgetRows} accent={ACCENT_VOTE} mode="net" />
      </Box>

      {/* BAND 2 — Opposition policies (single concern share, AS-IS 0–100), two cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", flex: 1, minHeight: 0 }}>
        <PolicyBandCard eyebrow="Opposition policies · concern" rows={oppRows(OPP_POLICIES_LEFT)} accent={ACCENT_OVERLAP} mode="share" />
        <PolicyBandCard eyebrow="Opposition policies · concern" rows={oppRows(OPP_POLICIES_RIGHT)} accent={ACCENT_OVERLAP} mode="share" />
      </Box>

      {/* BAND 3 — Message testing: each family's top-3 messages by LOCAL won-share */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", flex: 1, minHeight: 0 }}>
        {MESSAGE_FAMILIES.map((f) => (
          <MessageBandCard key={f.family} title={f.title} texts={f.texts} top3={msgTop3(f.family)} accent={ACCENT_MAPS} />
        ))}
      </Box>
    </Box>
    )}
    </Paper>
    </Stack>
  );
};

export default ConstituencyReportView;
